import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Mail,
  Phone,
  MapPin,
  Instagram,
  Send,
  Loader2,
  Facebook,
} from "lucide-react";
import { useCollectionData } from "../hooks/useCollectionData";
import { useSectionVisibility } from "../hooks/useSectionVisibility";
import { useBrandData } from "../hooks/useBrandData";
import { FAQS as FALLBACK_FAQS } from "../data/content";

const TikTokIcon = ({ size = 16, strokeWidth = 1.5 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
  </svg>
);

const getQuestion = (f) => f.question || f.q || "";
const getAnswer = (f) => f.answer || f.a || "";

export default function Contact() {
  const visibility = useSectionVisibility();
  const { brand } = useBrandData();
  const { data: faqs } = useCollectionData("faqs", FALLBACK_FAQS);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    date: "",
    service: "Akad Pengantin",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);

  const handle = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Mohon lengkapi nama dan nomor telepon.");
      return;
    }
    setSubmitting(true);

    // Construct WhatsApp message
    const message = [
      `Halo, saya ${form.name} ingin reservasi konsultasi.`,
      ``,
      `📋 *Detail Reservasi*`,
      `Nama: ${form.name}`,
      `Email: ${form.email || "-"}`,
      `Telepon: ${form.phone}`,
      `Tanggal Acara: ${form.date || "-"}`,
      `Layanan: ${form.service}`,
      `Pesan: ${form.message || "-"}`,
    ].join("\n");

    const waUrl = `https://wa.me/${brand.whatsapp}?text=${encodeURIComponent(message)}`;

    // Open WhatsApp
    window.open(waUrl, "_blank");

    setSubmitting(false);

    toast.success("Terbuka di WhatsApp! Silakan kirim pesannya.");

    setForm({
      name: "",
      email: "",
      phone: "",
      date: "",
      service: "Akad Pengantin",
      message: "",
    });
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-[#0a0908] py-28 sm:py-36 border-t border-[#d4af37]/10"
    >
      <div className="max-w-[1480px] mx-auto px-6 sm:px-10 lg:px-16 grid lg:grid-cols-12 gap-12 lg:gap-20">
        {/* Left: info + FAQ */}
        <div className="lg:col-span-5">
          <div className="mb-6 eyebrow">Mulai Percakapan</div>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#fdfbf7] font-light leading-[1.05] tracking-tight">
            Ceritakan hari
            <span className="block italic text-[#d4af37]">sempurna Anda.</span>
          </h2>
          <p className="mt-6 text-[#a6a196] leading-relaxed max-w-md">
            Setiap pengantin adalah sebuah cerita. Bagikan visi Anda, dan kami
            akan menyusun riasan yang merayakan Anda apa adanya.
          </p>

          <div className="mt-12 space-y-6">
            {[
              {
                icon: MapPin,
                label: "Atelier",
                value: brand.address,
              },
              {
                icon: Phone,
                label: "WhatsApp",
                value: brand.phone,
                href: `https://wa.me/${brand.whatsapp}`,
              },
              {
                icon: Mail,
                label: "Surel",
                value: brand.email,
                href: `mailto:${brand.email}`,
              },
              {
                icon: Instagram,
                label: "Instagram",
                value: brand.instagram,
                href: brand.socials.instagram,
              },
              {
                icon: Facebook,
                label: "Facebook",
                value: brand.facebook,
                href: brand.socials.facebook,
              },
              {
                icon: TikTokIcon,
                label: "TikTok",
                value: brand.tiktok,
                href: brand.socials.tiktok,
              },
            ].map((it, i) => {
              const Icon = it.icon;
              const Wrap = it.href ? "a" : "div";
              return (
                <Wrap
                  key={i}
                  href={it.href}
                  target={it.href ? "_blank" : undefined}
                  rel="noopener noreferrer"
                  data-testid={`contact-info-${it.label.toLowerCase()}`}
                  className="flex items-start gap-4 group"
                >
                  <div className="w-11 h-11 border border-[#d4af37]/30 flex items-center justify-center text-[#d4af37] group-hover:bg-[#d4af37] group-hover:text-[#0a0908] transition-all shrink-0">
                    <Icon size={16} strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.3em] text-[#d4af37] mb-1">
                      {it.label}
                    </p>
                    <p className="text-sm text-[#fdfbf7]/90 leading-relaxed">
                      {it.value}
                    </p>
                  </div>
                </Wrap>
              );
            })}
          </div>

          {/* FAQ */}
          {(!visibility.contact || visibility.contact) && (
            <div className="mt-14 border-t border-[#d4af37]/15 pt-10">
              <p className="text-[10px] tracking-[0.4em] uppercase text-[#d4af37] mb-6">
                Pertanyaan Umum
              </p>
              <div className="space-y-3">
                {faqs.map((f, i) => (
                  <div
                    key={i}
                    data-testid={`faq-${i}`}
                    className="border-b border-[#d4af37]/10 pb-3"
                  >
                    <button
                      onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                      data-testid={`faq-toggle-${i}`}
                      className="flex items-center justify-between w-full py-3 text-left group"
                    >
                      <span className="font-serif text-base sm:text-lg text-[#fdfbf7] group-hover:text-[#d4af37] transition-colors pr-4">
                        {getQuestion(f)}
                      </span>
                      <span
                        className={`text-[#d4af37] text-xl transition-transform shrink-0 ${
                          openFaq === i ? "rotate-45" : ""
                        }`}
                      >
                        +
                      </span>
                    </button>
                    <div
                      className={`grid transition-all duration-500 ${
                        openFaq === i
                          ? "grid-rows-[1fr] opacity-100"
                          : "grid-rows-[0fr] opacity-0"
                      }`}
                    >
                      <div className="overflow-hidden">
                        <p className="text-sm text-[#a6a196] leading-relaxed pb-3">
                          {getAnswer(f)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right: form */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="lg:col-span-7"
        >
          <div className="relative bg-[#15120f] border border-[#d4af37]/20 p-8 sm:p-12">
            <div className="absolute -top-px left-8 right-8 h-px bg-gradient-to-r from-transparent via-[#d4af37] to-transparent" />
            <h3 className="font-serif text-2xl sm:text-3xl text-[#fdfbf7] mb-2">
              Reservasi Konsultasi
            </h3>
            <p className="text-sm text-[#a6a196] mb-10">
              Kirim detail acara Anda langsung ke WhatsApp kami untuk respon
              lebih cepat.
            </p>

            <form
              onSubmit={onSubmit}
              className="space-y-7"
              data-testid="contact-form"
            >
              <div className="grid sm:grid-cols-2 gap-7">
                <div>
                  <label className="text-[10px] tracking-[0.3em] uppercase text-[#d4af37] block mb-2">
                    Nama Lengkap
                  </label>
                  <input
                    data-testid="form-name"
                    value={form.name}
                    onChange={handle("name")}
                    className="lux-input"
                    placeholder="Lina wati"
                  />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.3em] uppercase text-[#d4af37] block mb-2">
                    Surel
                  </label>
                  <input
                    data-testid="form-email"
                    type="email"
                    value={form.email}
                    onChange={handle("email")}
                    className="lux-input"
                    placeholder="anda@email.com"
                  />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.3em] uppercase text-[#d4af37] block mb-2">
                    Nomor Telepon
                  </label>
                  <input
                    data-testid="form-phone"
                    value={form.phone}
                    onChange={handle("phone")}
                    className="lux-input"
                    placeholder="+62 812 ..."
                  />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.3em] uppercase text-[#d4af37] block mb-2">
                    Tanggal Acara
                  </label>
                  <input
                    data-testid="form-date"
                    type="date"
                    value={form.date}
                    onChange={handle("date")}
                    className="lux-input"
                  />
                </div>
              </div>

              <div>
                <label className="text-[10px] tracking-[0.3em] uppercase text-[#d4af37] block mb-2">
                  Jenis Layanan
                </label>
                <select
                  data-testid="form-service"
                  value={form.service}
                  onChange={handle("service")}
                  className="lux-input bg-[#15120f]"
                >
                  <option>Akad Pengantin</option>
                  <option>Resepsi</option>
                  <option>Prewedding</option>
                  <option>Engagement</option>
                  <option>Graduation</option>
                  <option>Party & Editorial</option>
                  <option>Destination Wedding</option>
                </select>
              </div>

              <div>
                <label className="text-[10px] tracking-[0.3em] uppercase text-[#d4af37] block mb-2">
                  Ceritakan Visi Anda
                </label>
                <textarea
                  data-testid="form-message"
                  value={form.message}
                  onChange={handle("message")}
                  rows={4}
                  className="resize-none lux-input"
                  placeholder="Konsep, tema, atau pertanyaan apa pun..."
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="form-submit"
                className="group w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-[#d4af37] text-[#0a0908] px-12 py-4 text-xs tracking-[0.3em] uppercase font-semibold hover:bg-[#f0c950] transition-all disabled:opacity-60"
              >
                {submitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Membuka WhatsApp...
                  </>
                ) : (
                  <>
                    Kirim Reservasi
                    <Send
                      size={14}
                      className="transition-transform group-hover:translate-x-1"
                    />
                  </>
                )}
              </button>
            </form>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
